<template>
  <fn-page-container>
    <suspense v-if="page && template === 'product-listing'">
      <fn-product-listing
        :corrected-search-term="route.query.correctedText?.toString()"
        :product-listing-template="productListingTemplate"
        :page="page"
      />
    </suspense>
    <RenderCacheable
      v-else-if="page && template !== 'product-listing'"
      :cache-key="cacheKey"
    >
      <div>
        <fn-category
          v-if="page && template === 'category'"
          :nodes="templateData.nodes"
          :page="page"
        />
        <fn-advice-article
          v-else-if="page && template === 'advice'"
          :page="page"
        />
        <fn-advice-overview
          v-else-if="template === 'advice-overview'"
          :nodes="templateData.nodes"
          :page="page"
        />
        <fn-brand-overview
          v-else-if="page && template === 'brand-overview'"
          :brands="templateData.brands"
          :page="page"
        />
        <fn-contact
          v-else-if="template === 'contact'"
          :page="page"
        />
        <fn-content
          v-else-if="template === 'content'"
          :page="page"
        />
        <fn-newsletter-register
          v-else-if="template === 'newsletter-register'"
          :page="page"
        />
        <fn-newsletter-register-confirmation
          v-else-if="template === 'newsletter-confirmation'"
          :image="{
            src: NewsletterConfirmationImage,
            ...NewsletterConfirmationImageSize,
          }"
          :newsletter="newsletterStore.newsletterSubscription"
          :page="page"
        />
        <fn-newsletter-unsubscribe
          v-else-if="
            template === 'campaign-unsubscribe' ||
            template === 'campaign-unsubscribe-error' ||
            template === 'campaign-unsubscribe-confirmation'
          "
          :email="userStore.getDisplayUid"
          :page="page"
          is-campaign
          :error="template === 'campaign-unsubscribe-error'"
          :show-confirm="template === 'campaign-unsubscribe-confirmation'"
          @unsubscribe="handleCampaignUnsubscribe"
        />

        <fn-contact-confirmation
          v-else-if="template === 'contact-confirmation'"
          :image="{
            src: ConfirmationImage,
            ...ConfirmationImageSize,
          }"
          :page="page"
        />
        <fn-default-with-breadcrumbs
          v-else-if="template === 'default-with-breadcrumbs-sandlight'"
          :page="page"
          background-color="sand-light"
        />
        <fn-default-with-breadcrumbs
          v-else-if="template === 'default-with-breadcrumbs'"
          :page="page"
        />
        <fn-cms-flexible-layout-loader
          v-else-if="isFlexibleLayout"
          :key="page?.content.content[0].acf_fc_layout"
          :data="page?.content.content[0]"
          :page="page"
          :type="page?.content.content[0].acf_fc_layout"
        />
        <fn-friends
          v-else-if="template === 'friends'"
          :page="page"
        />
        <fn-friends-light
          v-else-if="template === 'friends-light'"
          :page="page"
        />
        <fn-shelters-overview
          v-else-if="template === 'shelters-overview'"
          :page="page"
        />
        <fn-product-recalls
          v-else-if="template === 'product-recall'"
          :page="page"
        />
        <fn-default
          v-else-if="page?.content"
          :page="page"
        />
      </div>
    </RenderCacheable>
  </fn-page-container>
</template>

<script lang="ts" setup>
/* eslint-disable import/no-duplicates */
import ConfirmationImage from '@/assets/img/contactconfirmation.jpg';
import ConfirmationImageSize from '@/assets/img/contactconfirmation.jpg?imageSize';
import NewsletterConfirmationImage from '@/assets/img/newsletter-confirmation.jpg';
import NewsletterConfirmationImageSize from '@/assets/img/newsletter-confirmation.jpg?imageSize';
/* eslint-enable import/no-duplicates */
import type { RouteLocationNormalizedLoadedGeneric } from '#vue-router';

type TemplateData = {
  brands?: Array<any>;
  filter?: Array<any>;
  nodes?: Array<any>;
  preSetting?: Array<any>;
};

// PageMeta `key` is necessary to trigger a full page reload when changing filters,
// by default it uses `route.path` which doesn't include filters from `route.query`
definePageMeta({
  name: 'catchall',
  key: (route) => route.fullPath,
  routeCmsPage: (route: RouteLocationNormalizedLoadedGeneric) => route.path,
});

const nuxtApp = useNuxtApp();
const { $api, $i18n, $log, $isocodes, runWithContext } = nuxtApp;
const route = useRoute();
const mainStore = useMainStore();
const userStore = useUserStore();
const newsletterStore = useNewsletterStore();

// Initialize with defaults
let page: any | undefined;
let template: string | undefined;
let templateData: TemplateData = {};
let productListingTemplate: Record<string, any> = {};
let isFlexibleLayout = false;
let cacheKey: string | undefined;

async function handleCampaignUnsubscribe() {
  const route = useRoute();

  try {
    await $api.hybris.campaign.unsubscribe(route.query.cryptId as string);
    return navigateTo($i18n.t('url.campaign.unsubscribe.confirmation'));
  } catch (e: any) {
    return navigateTo($i18n.t('url.campaign.unsubscribe.error'));
  }
}

// Main initialization function that can handle early returns
async function initializePage() {
  page = await mainStore.loadRoutePage(route);

  // Handle 404 page
  // Note: Instead of using `useCmsErrorPage`, we override the page and status code to prevent unnecessary redirects.
  // `useCmsErrorPage` should be used outside our page loading logic, since it works by throwing a 404 error which
  // is then handled as a completely separate request by nuxt.
  if (!page) {
    const event = useRequestEvent(nuxtApp);
    if (event) {
      page = await mainStore.loadCmsErrorPage();
      setResponseStatus(event, 404);
    } else {
      await useCmsErrorPage();
    }
    return;
  }

  // Page has been loaded so we can grab the template
  template = page?.content?.template;

  // Handle redirects
  if (page?.content?.should_redirect) {
    $log.debug('Should redirect page to: ', page.content.should_redirect);
    return navigateTo(page.content.should_redirect);
  }

  // Set page-specific global product recalls
  await mainStore.setProductRecalls(nuxtApp, page);

  // Continue with normal page processing
  await updatePageContent();
}

async function updatePageContent() {
  const flexibleLayout = page?.content?.content?.[0]?.acf_fc_layout;
  isFlexibleLayout = template === TEMPLATE_FLEXIBLE_LAYOUT && flexibleLayout !== 'new_pet';

  // Get template-specific page data
  templateData = await runWithContext(async () => {
    switch (template) {
      case 'category':
      case 'advice-overview':
        return useCategoryTemplate();
      case 'brand-overview':
        return useBrandOverviewTemplate({ $api, $isocodes, $route: route });
      default:
        return {};
    }
  });

  // Handle product-listing template
  if (template === 'product-listing') {
    productListingTemplate = await useProductListingTemplate(nuxtApp, page);

    // IMPORTANT NOTE: We should move away from calling `navigateTo` inside composables.
    // While it works in a lot of cases, it can become be very difficult to properly handle in complex cases.
    // The final argument against doing so while experiencing these difficulties, the official documentation
    // writes about using it in nuxt components and middlewares, but not in composables:
    // https://nuxt.com/docs/api/utils/navigate-to
    if (productListingTemplate?.navigateTo) {
      return navigateTo(productListingTemplate.navigateTo);
    }

    // Copy template page to the main page object
    if (productListingTemplate?.page) {
      page = productListingTemplate.page;
      delete productListingTemplate.page;
    }
  }
}

function updateCacheKey() {
  const templatesToCache = [
    'category',
    'advice',
    'advice-overview',
    'brand-overview',
    'content',
    'default-with-breadcrumbs',
    'default-with-breadcrumbs-sandlight',
    'default',
    '',
  ];

  if (templatesToCache.includes(template ?? '')) {
    cacheKey = generateCacheKeyPage(route.fullPath, page, {
      tealiumAudiences: userStore.tealiumAudiences,
      template,
    });
  }
}

// Page logic starts here
await initializePage();
updateCacheKey();
await usePageMeta({ ...(productListingTemplate || {}), page });

/**
 * For product listing template pages, wait for DOM updates and emit custom nuxt 'scroll:content:loaded' hook
 * in order to properly handle scroll position on navigate back.
 */
watchPostEffect(() => {
  nuxtApp.callHook('scroll:content:loaded');
});
</script>
